import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';

import {
  AuthStore,
  BookingStore,
  FeatureService,
  getGatewayMainDevices,
  InstallationPackageStatus,
  InstallationsStore,
  InstallationType,
  Platform,
  RemoteConfigStore,
  RemoteServiceStore,
} from '@store';

import {
  ActiveModeCommands,
  AggregatedStatus,
  Feature,
  InstallationItem,
  InstallationItemGatewayDevice,
  InstallationRecommendation,
  IsStringValueProperty,
} from '@vi-iot/client';

import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { PillType } from '../../../../shared/components/pill/pill.model';

import { EventNames } from '../../../../shared/directives/prevent-event/event-names';
import { matTooltipSetter } from '../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';

import { RemoteServiceService } from '../../../installation/remote-service/remote-service.service';
import { InstallationsService } from '../../../services/installations.service';
import { BrandLogo } from '../../../helpers/brand-logo';

@Component({
  selector: 'app-installation-item',
  templateUrl: './installation-item.component.html',
  styleUrls: ['./installation-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationItemComponent implements OnInit, OnDestroy {
  readonly MULTIGATEWAY_PLACEHOLDER_IMG_URL = '/assets/img/installation-multigateway.svg';
  readonly STANDALONE_PLACEHOLDER_IMG_URL = '/assets/img/devices/standalone.png';
  readonly platform = Platform;
  readonly pillType = PillType;
  readonly InstallationType = InstallationType;
  private aggregatedStatus: AggregatedStatus;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  brandLogo: string;

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  @Input() installation: InstallationItem;
  @Input() sortActive: string = undefined;
  @Input() isTableDesktopView: boolean;
  @Input() isFlatRate: boolean;

  operatingModes$: Observable<{
    circuits: string[];
    mode: { [key: string]: Feature<IsStringValueProperty, ActiveModeCommands> };
    isDhw: boolean;
  }>;
  installationPackage$: Observable<InstallationPackageStatus>;
  remoteInspectionRecommendation$: Observable<InstallationRecommendation>;

  isStandaloneGateway: boolean;
  mainDevices: InstallationItemGatewayDevice[];
  eventNames = EventNames;
  hasAccessToProPlusFeaturesByVkOrg: boolean;
  hasAccessToProPlusFeaturesByCompanyId: boolean;
  hasAccessToBooking: boolean;
  showRemoteInspectionRecommendation: boolean;
  serial: string;
  isMultigatewayInstallation: boolean;
  imageUri: string;
  installationTitle: string;
  canShowBuyProEncouragement: boolean;
  flatRatePermanentInspection: boolean;
  boilerSerial: string;
  isTrialPackageEnding: boolean;
  isContingentPackageEnding: boolean;

  constructor(
    public installationsStore: InstallationsStore,
    public remoteServiceService: RemoteServiceService,
    public authStore: AuthStore,
    private featureService: FeatureService,
    private remoteConfigStore: RemoteConfigStore,
    private remoteServiceStore: RemoteServiceStore,
    private bookingStore: BookingStore,
    private translate: TranslateService,
    private installationsService: InstallationsService
  ) {}

  ngOnInit(): void {
    this.isMultigatewayInstallation = this.installation?.gateways?.length > 1;
    this.boilerSerial = this.installationsService.getBoilerSerialOfGatewayMainDevice(
      this.installation?.gateways
    );
    if (!this.isMultigatewayInstallation) {
      const gatewaySerial = this.installation.gateways[0]?.serial;
      const mainDevice = getGatewayMainDevices(this.installation.gateways, false)[0];
      if (mainDevice) {
        this.installationsService.getInstallationOperatingModes(
          this.installation.id,
          gatewaySerial,
          mainDevice.id
        );

        this.operatingModes$ = this.featureService.selectOperatingModes(gatewaySerial).pipe(
          filter((operatingModes) => !!operatingModes),
          take(1)
        );
      }

      this.installationPackage$ = this.installationsStore
        .selectInstallationPackage(this.boilerSerial)
        .pipe(distinctUntilChanged(isEqual));

      this.remoteInspectionRecommendation$ = this.remoteServiceStore
        .selectInstallationRecommendation(this.boilerSerial)
        .pipe(distinctUntilChanged(isEqual));

      // aggregatedStatus
      this.installationsStore
        .selectAggregatedStatus(gatewaySerial)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((status) => {
          this.aggregatedStatus = status;
        });

      // isStandaloneGateway
      this.installationsStore
        .isStandaloneGateway(gatewaySerial)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((isStandaloneGateway) => {
          this.isStandaloneGateway = isStandaloneGateway;
        });
    }

    this.installationsStore
      .showRemoteInspectionRecommendation(this.installation.id)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((showRemoteInspectionRecommendation) => {
        this.showRemoteInspectionRecommendation = showRemoteInspectionRecommendation;
      });

    // mainDevices
    this.installationsStore
      .selectMainDevices(this.installation.id)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((mainDevices) => {
        this.mainDevices = mainDevices;
      });

    // hasAccessToProPlusFeaturesByVkOrg
    this.remoteConfigStore.hasAccessToProPlusFeaturesByVkOrg$
      .pipe(take(1))
      .subscribe((hasAccess) => {
        this.hasAccessToProPlusFeaturesByVkOrg = hasAccess;
      });

    // hasAccessToProPlusFeaturesByCompanyId
    this.remoteConfigStore.hasAccessToProPlusFeaturesByCompanyId$
      .pipe(take(1))
      .subscribe((hasAccess) => {
        this.hasAccessToProPlusFeaturesByCompanyId = hasAccess;
      });

    this.bookingStore.hasAccessToBooking$.pipe(take(1)).subscribe((hasAccess) => {
      this.hasAccessToBooking = hasAccess;
    });

    if (this.isMultigatewayInstallation) {
      this.serial = '';
    } else {
      this.serial = this.mainDevices?.[0]?.serial;
    }

    this.installationsStore
      .showAlertBeforeTrialPackageEnds(this.boilerSerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isTrialPackageEnding) => (this.isTrialPackageEnding = isTrialPackageEnding));

    this.installationsStore
      .showAlertBeforeContingentPackageEnds(this.boilerSerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (isContingentPackageEnding) => (this.isContingentPackageEnding = isContingentPackageEnding)
      );

    this.setInstallationTitle();
    this.setCanShowBuyProEncouragement();
    this.setImageUri();
    this.setBrandLogo();
  }

  isOverwriteSummaryWithErrorMessage(recommendation: { highestRiskValue: number }): boolean {
    const isOverwriteRecommendationValue =
      recommendation.highestRiskValue === 0 ||
      recommendation.highestRiskValue === 1 ||
      recommendation.highestRiskValue === 2;
    const shouldOverwrite =
      this.aggregatedStatus === AggregatedStatus.Error && isOverwriteRecommendationValue;

    return shouldOverwrite;
  }

  private setCanShowBuyProEncouragement(): void {
    const hasAccessToProPlusFeatures =
      this.hasAccessToProPlusFeaturesByVkOrg || this.hasAccessToProPlusFeaturesByCompanyId;

    this.canShowBuyProEncouragement = hasAccessToProPlusFeatures && this.hasAccessToBooking;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setInstallationTitle(): void {
    if (this.isMultigatewayInstallation) {
      this.installationTitle = `${this.translate.instant(
        '+INSTALLATIONS.INSTALLATION.MULTIGATEWAY_HEADER_LABEL'
      )}`;
    } else {
      this.installationTitle =
        this.mainDevices?.[0]?.marketingName ||
        this.translate.instant('+INSTALLATIONS.INSTALLATIONS.INSTALLATION_OF') +
          ' ' +
          this.installation?.ownerName;
    }
  }

  private setImageUri(): void {
    if (this.isMultigatewayInstallation) {
      this.imageUri = this.MULTIGATEWAY_PLACEHOLDER_IMG_URL;
    } else if (this.isStandaloneGateway) {
      this.imageUri = this.STANDALONE_PLACEHOLDER_IMG_URL;
    } else {
      this.imageUri = this.mainDevices?.[0]?.imageUrl || '';
    }
  }

  fetchBookingData(): void {
    this.bookingStore.getSubscriptionsDetails(this.boilerSerial, false);
  }

  private setBrandLogo(): void {
    const deviceRoles = this.mainDevices[0]?.roles;
    const installationDetails = this.installation?.gateways[0]?.devices.filter(
      (device) => !!device.marketingName
    );
    if (!this.mainDevices[0] && installationDetails.length !== 0) {
      this.brandLogo = BrandLogo.getBrandLogo(installationDetails[0].roles);
    } else {
      this.brandLogo = BrandLogo.getBrandLogo(deviceRoles);
    }
  }
}
